import * as z from 'zod';

import { zodDateOrString } from '../../utils';
import { MiCompanyCode } from './miCompanyCodes';

// Column H doubles as a status and a removal column. Blank (undefined) = performing,
// 00 = delinquent, 01 = reinstated; 02-99 mean the loan is leaving the MI portfolio.
// 02 (assumed) and 05 (claim paid) are unreachable in v1: no assumption or claims model.
export const MilarRemovalCode = z.enum([
  '00', // Delinquent
  '01', // Reinstated (was delinquent last cycle, now current)
  '02', // Assumed
  '03', // Sold / servicing transfer
  '04', // Paid in full, not REO
  '05', // Claim paid
  '06', // Chargeoff / loan uncollectable
  '99', // MI coverage terminated
]);
export type MilarRemovalCode = z.infer<typeof MilarRemovalCode>;

export const isMilarRemovalCode = (code: MilarRemovalCode | undefined): boolean =>
  code != null && code !== '00' && code !== '01';

export const MilarWorkoutType = z.enum([
  '1', // Standard forbearance (including unemployment programs)
  '2', // Trial modification forbearance
  '3', // Payment plan
  '4', // Loan modification (post trial period)
  '5', // Pre-foreclosure sale
  '6', // Deed-in-lieu
  '7', // Promise to pay
  '8', // Payment deferral
]);
export type MilarWorkoutType = z.infer<typeof MilarWorkoutType>;

// 1 (Under Review) and 4 (Denied) are unreachable in v1: Willow's workout plans have no
// under-review or denied lifecycle state.
export const MilarWorkoutStatus = z.enum([
  '1', // Under review
  '2', // Approved (approved but not fully executed; also v1's mapping for in-force plans)
  '3', // Closed (documents signed and executed, requirements fulfilled)
  '4', // Denied
  '5', // Failed (borrower failed to comply with a closed retention workout)
  '6', // Cancelled
]);
export type MilarWorkoutStatus = z.infer<typeof MilarWorkoutStatus>;

// The template's full program enumeration runs 1-44 plus per-insurer custom ranges. V1's
// best-effort mapping only ever emits these six (see milarProgramCode.ts).
export const MilarWorkoutProgramCode = z.enum([
  '9', // Proprietary mod
  '10', // Other - delinquent
  '11', // Other - performing (with a workout)
  '37', // Fannie Mae Flex Mod program
  '38', // Freddie Mac Flex Mod program
  '44', // Deferred payment plan
]);
export type MilarWorkoutProgramCode = z.infer<typeof MilarWorkoutProgramCode>;

/**
 * One MILAR-32 row in typed form. Money fields are dollar amounts and rates are decimals
 * (0.04125 for 4.125%): the row builder owns rendering. The Reporting Date column is not stored
 * per row because it derives from the persisted row's `monthYear`.
 */
export const MilarV1 = z.object({
  version: z.literal('1').describe('version'),

  miCompanyCode: MiCompanyCode,
  primaryCertificateNumber: z.string().describe('string'),
  poolCertificateNumber: z.string().optional().describe('string'),
  removalCode: MilarRemovalCode.optional(),

  currentPrincipalBalance: z.number().describe('currency'),
  principalAndInterestPayment: z.number().describe('currency'),

  primaryBorrowerName: z.string().describe('string'),
  servicerLoanNumber: z.string().describe('string'),
  servicerName: z.string().describe('string'),
  investorName: z.string().optional().describe('string'),
  investorLoanNumber: z.string().optional().describe('string'),

  propertyStreet: z.string().optional().describe('string'),
  propertyCity: z.string().optional().describe('string'),
  propertyState: z.string().optional().describe('string'),
  propertyZip: z.string().optional().describe('string'),

  nextPaymentDueDate: zodDateOrString.describe('date'),

  workoutType: MilarWorkoutType.optional(),
  workoutStatus: MilarWorkoutStatus.optional(),
  workoutStatusDate: zodDateOrString.optional().describe('date'),
  workoutProgramCode: MilarWorkoutProgramCode.optional(),

  currentInterestRate: z.number().describe('number'),
  interestAccrualRate: z.number().describe('number'),
  originalClosingDate: zodDateOrString.optional().describe('date'),

  borrowerProceedingPending: z.boolean().describe('boolean'),
  borrowerProceedingStartDate: zodDateOrString.optional().describe('date'),
  borrowerProceedingEndDate: zodDateOrString.optional().describe('date'),

  servicingTransfereeName: z.string().optional().describe('string'),

  // Retained so the next cycle's state machine and program-code mapping don't have to re-derive it.
  monthsDelinquent: z.number().int().describe('number'),
});
export type MilarV1 = z.infer<typeof MilarV1>;

export const Milar = MilarV1;
export type Milar = z.infer<typeof Milar>;
